import React, { useState, useEffect } from 'react';
import { getAllUsers, getUserGameDetails, getAllGameStories, toggleUserActive, updateUserStoryId } from '../../utils/api';
import LoadingState from '../../components/LoadingState';

const UserMonitor = () => {
  const [users, setUsers] = useState([]);
  const [stories, setStories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [isUpdatingStory, setIsUpdatingStory] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState('');
  
  // Fetch data on mount
  useEffect(() => {
    fetchUsers();
    fetchStories();
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  
  // Fetch all users
  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      console.log('Fetching all users...');
      const response = await getAllUsers();
      
      if (!response) {
        console.error('No response received from getAllUsers API call');
        setError('ユーザーの読み込みに失敗しました: サーバーからの応答がありません');
        return;
      }
      
      console.log('API response:', response);
      
      if (response && response.success) {
        console.log('Fetched users successfully:', response.data);
        
        if (!response.data || !Array.isArray(response.data)) {
          console.error('No user data array in response:', response);
          setError('ユーザーの読み込みに失敗しました: データ形式が無効です');
          setUsers([]);
          return;
        }
        
        // Transform the user data for display
        const transformedUsers = transformUserData(response.data);
        console.log('Transformed user data:', transformedUsers);
        
        if (transformedUsers.length === 0) {
          console.warn('No users found in the response');
          // Don't set an error, just show empty state
        }
        
        setUsers(transformedUsers);
      } else {
        console.error('API returned error:', response.message);
        setError(`ユーザーの読み込みに失敗しました: ${response.message || '不明なエラー'}`);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(`ユーザーの読み込みに失敗しました: ${err.message || '不明なエラー'}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch all stories
  const fetchStories = async () => {
    try {
      console.log('Fetching all game stories...');
      const response = await getAllGameStories();
      
      if (!response) {
        console.warn('No response received from getAllGameStories');
        return;
      }
      
      if (response && response.success) {
        console.log('Fetched stories successfully:', response.data);
        
        if (!response.data || !Array.isArray(response.data)) {
          console.warn('No story data array in response');
          setStories([]);
          return;
        }
        
        setStories(response.data || []);
      } else {
        console.warn('Failed to fetch stories:', response?.message);
      }
    } catch (err) {
      console.error('Error fetching stories:', err);
      // Don't set error state here to avoid blocking user display
    }
  };
  
  // Fetch details for a specific user
  const fetchUserDetails = async (userId) => {
    if (!userId) {
      console.error('No user ID provided for details');
      return;
    }
    
    setIsLoadingDetails(true);
    setUserDetails(null);
    setError('');
    
    try {
      console.log(`Fetching details for user: ${userId}`);
      const response = await getUserGameDetails(userId);
      
      if (!response) {
        throw new Error('No response received from API');
      }
      
      console.log('User details API response:', response);
      
      if (response && response.success) {
        if (!response.data) {
          throw new Error('No data in API response');
        }
        
        // Check for story ID in multiple possible locations
        const userStoryId = response.data.user?.storyId;
        const progressStoryId = response.data.progress?.storyId;
        const storyId = userStoryId || progressStoryId;
        
        // If we have a story ID but no story object, try to find and attach it manually
        if (storyId && !response.data.story) {
          const matchedStory = findStoryById(storyId);
          if (matchedStory) {
            console.log('Manually matched story:', matchedStory);
            response.data.story = matchedStory;
          } else if (storyId) {
            // Create a minimal story object with the ID if we can't find a match
            console.log('Creating minimal story object with ID:', storyId);
            response.data.story = {
              storyId: storyId,
              title: `Story ID: ${storyId}`
            };
          }
        }
        
        // Ensure the user object has the storyId even if it's only in the progress
        if (response.data.user && !response.data.user.storyId && progressStoryId) {
          response.data.user.storyId = progressStoryId;
        }
        
        setUserDetails(response.data);
      } else {
        throw new Error(response.message || 'Failed to load user details');
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(`ユーザー詳細の読み込みに失敗しました: ${err.message || '不明なエラー'}`);
    } finally {
      setIsLoadingDetails(false);
    }
  };
  
  // Story matching function that handles various ID formats
  const findStoryById = (storyId) => {
    if (!storyId) return null;
    if (!stories || stories.length === 0) {
      console.warn('No stories available for matching');
      return null;
    }
    
    console.log(`Finding story with ID: ${storyId}`);
    console.log('Available stories:', stories);
    
    // Try direct match
    let match = stories.find(s => s.storyId === storyId);
    if (match) {
      console.log('Found direct match:', match);
      return match;
    }
    
    // Try case-insensitive match
    match = stories.find(s => 
      s.storyId && storyId && 
      s.storyId.toString().toLowerCase() === storyId.toString().toLowerCase()
    );
    if (match) {
      console.log('Found case-insensitive match:', match);
      return match;
    }
    
    // For MongoDB ObjectId, try matching just the last part after the dash
    if (storyId.includes('-')) {
      const idPart = storyId.split('-').pop();
      match = stories.find(s => s.storyId && s.storyId.endsWith(idPart));
      if (match) {
        console.log('Found partial ID match:', match);
        return match;
      }
    }
    
    // Try matching just the beginning part of the ID
    match = stories.find(s => s.storyId && storyId && 
      (s.storyId.startsWith(storyId) || storyId.startsWith(s.storyId)));
    if (match) {
      console.log('Found prefix match:', match);
      return match;
    }
    
    // If all else fails, just return the first story if available
    if (stories.length > 0) {
      console.log('Using first available story as fallback');
      return stories[0];
    }
    
    console.warn('No story found for ID:', storyId);
    return null;
  };
  
  // Handle toggling user active status
  const handleToggleActive = async (userId, currentActiveState) => {
    if (!userId) {
      console.error('No user ID provided for toggle');
      setError('ユーザーステータスを切り替えられません: ユーザーIDがありません');
      return;
    }
    
    setIsTogglingActive(true);
    setError('');
    setSuccessMessage(''); // Clear any previous success messages
    
    try {
      console.log(`Toggling active status for user: ${userId}, current status: ${currentActiveState}`);
      const response = await toggleUserActive(userId);
      
      if (!response) {
        throw new Error('サーバーからの応答がありません');
      }
      
      console.log('Toggle active API response:', response);
      
      if (response && response.success && response.data) {
        // Extract the new active status from the response
        const newActiveStatus = response.data.isActive === true;
        console.log(`User ${userId} new active status: ${newActiveStatus}`);
        
        // Update the user in the list with the EXACT value from the server
        setUsers(prevUsers => {
          // Make a copy of the users array
          const updatedUsers = prevUsers.map(user => {
            if (user.id === userId || user.id === response.data.userId || user.id === response.data._id) {
              console.log(`Updating user ${user.id} active status from ${user.isActive} to ${newActiveStatus}`);
              // Return a new object with the updated isActive status
              return { 
                ...user, 
                isActive: newActiveStatus 
              };
            }
            return user;
          });
          
          console.log('Updated users list:', updatedUsers);
          return updatedUsers;
        });
        
        // Update selected user if it's the one being toggled
        if (selectedUser && (selectedUser.id === userId || selectedUser.id === response.data.userId || selectedUser.id === response.data._id)) {
          console.log(`Updating selected user active status to ${newActiveStatus}`);
          setSelectedUser(prevUser => ({ 
            ...prevUser, 
            isActive: newActiveStatus 
          }));
        }
        
        setSuccessMessage(`ユーザーが${response.data.isActive ? 'アクティブ化' : '停止'}されました`);
        
        // Refresh all users data to ensure we have the latest
        setTimeout(() => {
          console.log('Refreshing user data after toggle...');
          fetchUsers();
        }, 1000);
        
        // Refresh user details if viewing that user
        if (selectedUser && (selectedUser.id === userId || selectedUser.id === response.data.userId || selectedUser.id === response.data._id)) {
          setTimeout(() => {
            console.log('Refreshing selected user details...');
            fetchUserDetails(userId);
          }, 1200);
        }
      } else {
        // Extract and display error message from the response if available
        const errorMsg = response.message || 'ステータスの更新に失敗しました';
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('Error toggling user status:', err);
      // Get a more descriptive error message
      const errorMessage = err.response?.data?.message || err.message || '不明なエラー';
      setError(`ユーザーステータスの更新に失敗しました: ${errorMessage}`);
      
      // Refresh the user list anyway to get the current state
      setTimeout(() => {
        console.log('Refreshing user data after error...');
        fetchUsers();
      }, 1000);
    } finally {
      setIsTogglingActive(false);
    }
  };
  
  const handleUserSelect = (user) => {
    if (!user || !user.id) {
      console.error('Invalid user selected:', user);
      setError('ユーザー詳細を読み込めません: 無効なユーザーデータ');
      return;
    }
    
    console.log('Selected user:', user);
    setSelectedUser(user);
    fetchUserDetails(user.id);
  };
  
  const getFilteredUsers = () => {
    let filtered = [...users];
    
    // Apply active/inactive filter
    if (activeFilter === 'active') {
      filtered = filtered.filter(user => user.isActive);
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(user => !user.isActive);
    }
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        (user.name && user.name.toLowerCase().includes(query)) || 
        (user.room && user.room.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };
  
  const getStoryName = (storyId) => {
    if (!storyId) return 'ストーリーなし';
    
    const story = findStoryById(storyId);
    return story ? story.title : `ストーリーID: ${storyId}`;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return 'Invalid Date';
    }
  };
  
  const getProgressPercentage = (progress) => {
    if (!progress) return 0;
    
    if (progress.questionsAnswered && progress.questionsAnswered.length > 0) {
      // Get the story to determine total questions
      const story = findStoryById(progress.storyId);
      if (story && story.questionsAnswers) {
        return Math.round((progress.questionsAnswered.length / story.questionsAnswers.length) * 100);
      }
    }
    
    return 0;
  };
  
  // Function to transform API data into a more usable format
  const transformUserData = (usersData) => {
    if (!usersData || !Array.isArray(usersData)) {
      console.error('Invalid user data:', usersData);
      return [];
    }
    
    console.log('Raw user data from API:', usersData);
    
    return usersData.map(user => {
      // Ensure we have a valid user object
      if (!user) {
        console.error('Invalid user object in array');
        return {
          id: 'unknown-' + Math.random().toString(36).substr(2, 9),
          name: 'Unknown Guest',
          room: 'No Room',
          isActive: false,
          storyId: null,
          startTime: new Date(),
          lastActivity: new Date()
        };
      }
      
      console.log('Processing user data:', user);
      
      try {
        // Ensure we have a valid ID (try multiple possible field names)
        const userId = user._id || user.userId || user.id || null;
        if (!userId) {
          console.warn('User missing ID, generating placeholder');
        }
        
        // Extract room number information - check all possible field names
        let roomInfo = null;
        if (user.roomNumber !== undefined) {
          roomInfo = user.roomNumber;
        } else if (user.room !== undefined) {
          roomInfo = user.room;
        }
        
        console.log(`Room info for user ${userId}:`, roomInfo);
        const roomNumber = roomInfo ? String(roomInfo) : 'No Room';
        
        // Extract user name - check all possible field names
        const userName = user.guestName || user.name || 'Unknown Guest';
        
        // Determine if the user is active - explicit boolean check
        // Convert to true only if explicitly true
        const isUserActive = user.isActive === true;
        
        // Extract the storyId if available
        const storyId = user.storyId || null;
        
        const result = {
          id: userId || ('temp-' + Math.random().toString(36).substr(2, 9)),
          name: userName,
          room: roomNumber,
          isActive: isUserActive,
          storyId: storyId,
          progress: user.progress || null,
          startTime: user.createdAt || user.startTime || new Date(),
          lastActivity: user.updatedAt || user.lastActivity || user.lastActivityAt || new Date()
        };
        
        console.log(`Transformed user - ID: ${result.id}, Name: ${result.name}, Room: ${result.room}, Active: ${result.isActive}, StoryId: ${result.storyId}`);
        return result;
      } catch (err) {
        console.error('Error processing user:', err);
        // Return a placeholder in case of processing error
        return {
          id: 'error-' + Math.random().toString(36).substr(2, 9),
          name: user.guestName || user.name || 'ユーザー処理エラー',
          room: user.roomNumber || user.room || 'エラー',
          isActive: false,
          storyId: null,
          startTime: new Date(),
          lastActivity: new Date()
        };
      }
    });
  };

  // Format user location to show room number
  const getUserLocation = (user) => {
    if (!user) return '場所情報なし';
    
    if (user.room && user.room !== 'No Room') {
      return `部屋: ${user.room}`;
    }
    
    return '部屋情報なし';
  };
  
  // Function to handle updating a user's story ID
  const handleUpdateStory = async (userId) => {
    if (!userId || !selectedStoryId) {
      console.error('User ID and Story ID are required');
      setError('更新にはユーザーIDとストーリーIDが必要です');
      return;
    }
    
    setIsUpdatingStory(true);
    setError('');
    setSuccessMessage('');
    
    try {
      console.log(`Updating story ID for user ${userId} to ${selectedStoryId}`);
      const response = await updateUserStoryId(userId, selectedStoryId);
      
      if (!response) {
        throw new Error('No response received from API');
      }
      
      console.log('Update story API response:', response);
      
      if (response && response.success) {
        // Update the user in the list
        setUsers(prevUsers => {
          return prevUsers.map(user => {
            if (user.id === userId) {
              return { ...user, storyId: selectedStoryId };
            }
            return user;
          });
        });
        
        // Update selected user if it's the one being updated
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(prevUser => ({ ...prevUser, storyId: selectedStoryId }));
        }
        
        // Refresh user details
        fetchUserDetails(userId);
        
        setSuccessMessage('ストーリーが正常に更新されました');
        
        // Clear the selected story ID
        setSelectedStoryId('');
      } else {
        throw new Error(response.message || 'Failed to update story');
      }
    } catch (err) {
      console.error('Error updating story:', err);
      setError(`ストーリーの更新に失敗しました: ${err.message || '不明なエラー'}`);
    } finally {
      setIsUpdatingStory(false);
    }
  };
  
  const renderUserDetails = () => {
    if (!selectedUser) {
      return (
        <div className="p-4 bg-gray-100 rounded-md text-center">
          <p className="text-gray-600">詳細を表示するユーザーを選択してください</p>
        </div>
      );
    }
    
    if (isLoadingDetails) {
      return <LoadingState message="ユーザー詳細を読み込み中..." />;
    }
    
    if (!userDetails) {
      return (
        <div className="p-4 bg-red-100 rounded-md">
          <p className="text-red-600">ユーザー詳細の読み込みに失敗しました</p>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-md shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">
            ユーザー詳細: {selectedUser.name}
          </h2>
          <span className="text-sm text-gray-500">
            {getUserLocation(selectedUser)}
          </span>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2 flex justify-between items-center">
            ゲーム進捗
            <button 
              onClick={fetchStories} 
              className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 py-1 px-2 rounded"
              title="ストーリーデータを更新"
            >
              ストーリー更新
            </button>
          </h3>
          
          {/* Story update form */}
          <div className="bg-gray-50 p-3 rounded-md mb-4">
            <h4 className="text-sm font-medium mb-2">ストーリー更新</h4>
            <div className="flex items-center space-x-2">
              <select
                className="border border-gray-300 rounded p-1 text-sm flex-1"
                value={selectedStoryId}
                onChange={(e) => setSelectedStoryId(e.target.value)}
              >
                <option value="">ストーリーを選択</option>
                {stories.map(story => (
                  <option key={story.storyId} value={story.storyId}>
                    {story.title}
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleUpdateStory(selectedUser.id)}
                disabled={isUpdatingStory || !selectedStoryId}
                className={`px-3 py-1 text-sm rounded ${
                  isUpdatingStory || !selectedStoryId
                    ? 'bg-gray-200 text-gray-500'
                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                }`}
              >
                {isUpdatingStory ? '更新中...' : '更新'}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-100 p-3 rounded-md">
              <p className="text-sm text-gray-600">ストーリー</p>
              <p className="font-medium">
                {userDetails.story ? (
                  <span className="text-green-700">{userDetails.story.title}</span>
                ) : 
                userDetails.progress?.storyId ? (
                  getStoryName(userDetails.progress.storyId)
                ) : 
                userDetails.user?.storyId ? (
                  getStoryName(userDetails.user.storyId)
                ) : (
                  "ストーリーなし"
                )}
              </p>
              {(userDetails.progress?.storyId || userDetails.user?.storyId) && !userDetails.story && (
                <p className="text-xs text-gray-500 mt-1">
                  ID: {userDetails.progress?.storyId || userDetails.user?.storyId}
                </p>
              )}
            </div>
            <div className="bg-gray-100 p-3 rounded-md">
              <p className="text-sm text-gray-600">ステータス</p>
              <p className="font-medium">
                {userDetails.progress?.gameCompleted ? (
                  <span className="text-green-600">完了</span>
                ) : (
                  <span className="text-blue-600">進行中</span>
                )}
              </p>
            </div>
            <div className="bg-gray-100 p-3 rounded-md">
              <p className="text-sm text-gray-600">スコア</p>
              <p className="font-medium">{userDetails.progress?.totalScore || 0} ポイント</p>
            </div>
            <div className="bg-gray-100 p-3 rounded-md">
              <p className="text-sm text-gray-600">最終アクティビティ</p>
              <p className="font-medium">{formatDate(userDetails.progress?.lastActivityAt || userDetails.user?.updatedAt)}</p>
            </div>
          </div>
        </div>
        
        {userDetails.progress?.questionsAnswered && userDetails.progress.questionsAnswered.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">回答済みの質問</h3>
            <div className="overflow-auto max-h-60">
              {userDetails.progress.answers && userDetails.progress.answers.map((answer, index) => (
                <div key={index} className="mb-3 p-3 bg-gray-50 rounded-md">
                  <div className="flex justify-between mb-1">
                    <p className="font-medium">質問 {index + 1}</p>
                    <p className={answer.isCorrect ? "text-green-600" : "text-red-600"}>
                      {answer.isCorrect ? "正解" : "不正解"}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">回答: {answer.answer}</p>
                  {answer.fileUploadUrl && (
                    <a 
                      href={answer.fileUploadUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm hover:underline"
                    >
                      アップロードファイルを表示
                    </a>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    試行回数: {answer.attemptCount || 1}, ポイント: {answer.pointsEarned || 0}
                  </p>
                </div>
              ))}
              {!userDetails.progress.answers && (
                <div className="p-3 bg-gray-50 rounded-md">
                  <p className="text-gray-600">詳細な回答データがありません</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {userDetails.progress?.chatHistory && userDetails.progress.chatHistory.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-2">チャット履歴</h3>
            <div className="overflow-auto max-h-80 bg-gray-50 p-3 rounded-md">
              {userDetails.progress.chatHistory.map((message, index) => (
                <div 
                  key={index} 
                  className={`mb-2 p-2 rounded-md ${
                    message.isUser 
                      ? "bg-blue-100 ml-8" 
                      : "bg-gray-200 mr-8"
                  }`}
                >
                  <p className="text-sm font-medium mb-1">
                    {message.isUser ? "ユーザー" : "システム"}
                  </p>
                  <p className="text-sm">{message.text}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatDate(message.timestamp)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  const handleRetry = () => {
    console.log('Retrying API calls...');
    setError('');
    setIsLoading(true);
    
    // Reset state that might be in a bad state
    if (selectedUser && isLoadingDetails) {
      setIsLoadingDetails(false);
    }
    
    if (isTogglingActive) {
      setIsTogglingActive(false);
    }
    
    // Fetch fresh data
    Promise.all([
      fetchUsers(),
      fetchStories()
    ]).finally(() => {
      setIsLoading(false);
      
      // If a user was selected, refresh their details
      if (selectedUser) {
        fetchUserDetails(selectedUser.id);
      }
    });
  };
  
  if (isLoading && !selectedUser) {
    return <LoadingState message="ユーザーデータを読み込み中..." />;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ユーザーモニター</h1>
        <p className="text-gray-600 mt-1">ユーザーの進捗状況と利用状況を追跡</p>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6">
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          <div className="flex justify-between items-center">
            <p>{error}</p>
            <button 
              onClick={handleRetry} 
              className="px-3 py-1 bg-red-200 hover:bg-red-300 rounded-md text-sm font-medium"
            >
              再試行
            </button>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
          {successMessage}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users List */}
        <div className="lg:col-span-1 bg-white p-4 rounded-lg shadow">
          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">ユーザー</h2>
              <button
                onClick={handleRetry}
                className="text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 py-1 px-2 rounded flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                更新
              </button>
            </div>
            
            {/* Filters */}
            <div className="flex flex-col space-y-2 mb-4">
              <input
                type="text"
                placeholder="名前または部屋番号で検索..."
                className="border border-gray-300 rounded p-2"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              
              <div className="flex space-x-2">
                <button
                  className={`px-2 py-1 rounded text-sm ${activeFilter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setActiveFilter('all')}
                >
                  全て
                </button>
                <button
                  className={`px-2 py-1 rounded text-sm ${activeFilter === 'active' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setActiveFilter('active')}
                >
                  アクティブ
                </button>
                <button
                  className={`px-2 py-1 rounded text-sm ${activeFilter === 'inactive' ? 'bg-red-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => setActiveFilter('inactive')}
                >
                  承認待ち
                </button>
              </div>
            </div>
            
            {/* Users List */}
            {isLoading ? (
              <LoadingState message="ユーザーを読み込み中..." />
            ) : (
              <div className="overflow-y-auto max-h-[600px]">
                {getFilteredUsers().length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {getFilteredUsers().map((user) => (
                      <li 
                        key={user.id} 
                        className={`p-2 cursor-pointer hover:bg-gray-50 ${selectedUser?.id === user.id ? 'bg-blue-50' : ''}`}
                        onClick={() => handleUserSelect(user)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-gray-600">{getUserLocation(user)}</p>
                            {user.storyId && (
                              <p className="text-xs text-gray-600">
                                ストーリー: {getStoryName(user.storyId)}
                              </p>
                            )}
                            <p className="text-xs text-gray-500">
                              開始: {formatDate(user.startTime)}
                            </p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span 
                              className={`px-2 py-1 rounded-full text-xs ${
                                user.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {user.isActive ? 'アクティブ' : '承認待ち'}
                            </span>
                            {user.progress && (
                              <span className="text-xs mt-1">
                                {getProgressPercentage(user.progress)}% 完了
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-center py-4 text-gray-500">条件に一致するユーザーがいません</p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* User Details */}
        <div className="lg:col-span-2 bg-white p-4 rounded-lg shadow">
          {selectedUser ? (
            isLoadingDetails ? (
              <LoadingState message="ユーザー詳細を読み込み中..." />
            ) : (
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-bold">{selectedUser.name}</h2>
                  </div>
                  <button
                    onClick={() => handleToggleActive(selectedUser.id, selectedUser.isActive)}
                    disabled={isTogglingActive}
                    className={`px-3 py-1 rounded ${
                      selectedUser.isActive
                        ? 'bg-red-100 text-red-800 hover:bg-red-200'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    }`}
                  >
                    {isTogglingActive ? '更新中...' : selectedUser.isActive ? '停止' : 'アクティブ化'}
                  </button>
                </div>
                
                {/* User location details */}
                <div className="mb-4 p-3 bg-gray-50 rounded-md">
                  <h3 className="font-semibold mb-2">ユーザーの場所</h3>
                  {selectedUser.room && selectedUser.room !== 'No Room' ? (
                    <div className="text-sm">
                      <span className="font-medium">部屋番号:</span> {selectedUser.room}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">部屋情報がありません</div>
                  )}
                </div>
                
                {renderUserDetails()}
              </div>
            )
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>詳細を表示するユーザーを選択してください</p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserMonitor; 